import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Envase.css';

function Envase() {
  const [datosFiltrados, setDatosFiltrados] = useState([]);   // filas de la pagina actual
  const [busqueda, setBusqueda] = useState('');
  const [ordenAsc, setOrdenAsc] = useState(true);
  const [columnaOrden, setColumnaOrden] = useState('idEnvase');

  // Paginacion (server-side)
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formulario, setFormulario] = useState({
    idEnvase: '',
    nomEnvase: ''
  });
  const [errores, setErrores] = useState({});

  // Importar Excel
  const inputImportarRef = useRef(null);
  const [importando, setImportando] = useState(false);

  const API_URL = '/api/tenvase';

  // Carga paginada desde el backend
  const cargarDatos = async (opts = {}) => {
    const _pagina    = opts.pagina    ?? pagina;
    const _busqueda  = opts.busqueda  ?? busqueda;
    const _col       = opts.col       ?? columnaOrden;
    const _asc       = opts.asc       ?? ordenAsc;
    const _porPagina = opts.porPagina ?? porPagina;
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: {
          busqueda: _busqueda,
          pagina: _pagina,
          porPagina: _porPagina,
          ordenCol: _col,
          ordenDir: _asc ? 'asc' : 'desc'
        }
      });
      const d = response.data;
      setDatosFiltrados(d.filas || []);
      setTotal(d.total || 0);
      setTotalPaginas(d.totalPaginas || 1);
      setPagina(d.pagina || 1);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar los datos');
    }
  };

  // Carga inicial
  useEffect(() => {
    cargarDatos({ pagina: 1 });
    // eslint-disable-next-line
  }, []);

  // Busqueda con debounce -> vuelve a pagina 1
  useEffect(() => {
    const t = setTimeout(() => {
      cargarDatos({ pagina: 1, busqueda });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [busqueda]);

  const ordenarPor = (columna) => {
    const esAsc = columnaOrden === columna ? !ordenAsc : true;
    setColumnaOrden(columna);
    setOrdenAsc(esAsc);
    cargarDatos({ pagina: 1, col: columna, asc: esAsc });
  };

  const irAPagina = (p) => {
    if (p < 1 || p > totalPaginas) return;
    setPagina(p);
    cargarDatos({ pagina: p });
  };

  const cambiarPorPagina = (n) => {
    setPorPagina(n);
    setPagina(1);
    cargarDatos({ pagina: 1, porPagina: n });
  };

  // Exporta TODO lo que matchea la busqueda (no solo la pagina visible)
  const exportarExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: {
          busqueda,
          pagina: 1,
          porPagina: 100000,      // traer todo lo filtrado
          ordenCol: columnaOrden,
          ordenDir: ordenAsc ? 'asc' : 'desc'
        }
      });
      const datosExportar = (response.data.filas || []).map(item => ({
        'ID Envase': item.idEnvase,
        'Nombre Envase': item.nomEnvase
      }));
      const ws = XLSX.utils.json_to_sheet(datosExportar);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Envases');
      XLSX.writeFile(wb, 'envases.xlsx');
    } catch (e) {
      console.error(e);
      alert('Error al exportar');
    }
  };

  // ---------- Importar Excel ----------
  // Acepta archivos con estas columnas (case-insensitive, buscar la 1a fila
  // que contenga id_envase / nom_envase):
  //   id_envase | nom_envase      (formato de la BD)
  //   ID Envase | Nombre Envase   (formato exportado por esta pantalla)
  const abrirImportar = () => inputImportarRef.current?.click();

  const onArchivoImportar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportando(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const primerSheet = wb.Sheets[wb.SheetNames[0]];
      // Leer TODO como matriz de strings (por si hay filas de header repetidos
      // o comentarios arriba, como en el modelo tenvase.xlsx)
      const rows = XLSX.utils.sheet_to_json(primerSheet, { header: 1, defval: '' });

      // Detectar la fila de headers real: buscamos la que contenga la palabra
      // 'envase' en la 1a celda y 'envase' en la 2a
      let headerIdx = -1;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] || [];
        const c0 = String(r[0] || '').toLowerCase();
        const c1 = String(r[1] || '').toLowerCase();
        if (c0.includes('envase') && c1.includes('envase')) {
          // Aceptar tanto 'id_envase / nom_envase' como 'ID Envase / Nombre Envase'
          headerIdx = i;
          break;
        }
      }
      if (headerIdx < 0) {
        alert('No se encontro fila de encabezados. El archivo debe tener columnas para "id_envase" y "nom_envase".');
        return;
      }

      const filas = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const r = rows[i] || [];
        const id  = String(r[0] || '').trim();
        const nom = String(r[1] || '').trim();
        if (!id && !nom) continue;   // saltar filas vacias
        filas.push({ idEnvase: id, nomEnvase: nom });
      }

      if (filas.length === 0) {
        alert('El archivo no tiene filas de datos.');
        return;
      }

      const conf = window.confirm(
        `Se importaran ${filas.length} envase(s). ` +
        `Los ID existentes se actualizaran; los nuevos se crearan. ` +
        `No se eliminaran envases. Continuar?`
      );
      if (!conf) return;

      const { data } = await axios.post('/api/tenvase/importar', filas);
      const lineas = [
        `Import completado.`,
        `  Creados:      ${data.creados}`,
        `  Actualizados: ${data.actualizados}`,
        `  Ignorados:    ${data.ignorados}`
      ];
      if (data.errores?.length) {
        lineas.push('', 'Errores/advertencias:');
        (data.errores.slice(0, 10)).forEach(m => lineas.push('  - ' + m));
        if (data.errores.length > 10) lineas.push(`  ... y ${data.errores.length - 10} mas`);
      }
      alert(lineas.join('\n'));
      cargarDatos();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error al importar: ' + err.message);
    } finally {
      setImportando(false);
      if (inputImportarRef.current) inputImportarRef.current.value = '';
    }
  };

  const abrirModalNuevo = () => {
    setFormulario({ idEnvase: '', nomEnvase: '' });
    setModoEdicion(false);
    setModalAbierto(true);
  };

  const abrirModalEditar = (item) => {
    setFormulario({ idEnvase: item.idEnvase, nomEnvase: item.nomEnvase });
    setModoEdicion(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setFormulario({ idEnvase: '', nomEnvase: '' });
    setErrores({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario({
      ...formulario,
      [name]: value
    });
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errores[name]) {
      setErrores({
        ...errores,
        [name]: ''
      });
    }
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    // Validar ID (requerido, 3 caracteres exactos, solo letras y números)
    if (!modoEdicion) {
      if (!formulario.idEnvase.trim()) {
        nuevosErrores.idEnvase = 'El ID es requerido';
      } else if (formulario.idEnvase.trim().length !== 3) {
        nuevosErrores.idEnvase = 'El ID debe tener exactamente 3 caracteres';
      } else if (!/^[a-zA-Z0-9]+$/.test(formulario.idEnvase.trim())) {
        nuevosErrores.idEnvase = 'El ID solo puede contener letras y números';
      }
      // La unicidad del ID la valida el backend (PK). Al crear, si ya existe,
      // el UPSERT/insert devuelve error y se muestra al guardar.
    }

    // Validar nombre (requerido y mínimo 3 caracteres)
    if (!formulario.nomEnvase.trim()) {
      nuevosErrores.nomEnvase = 'El nombre es requerido';
    } else if (formulario.nomEnvase.trim().length < 3) {
      nuevosErrores.nomEnvase = 'El nombre debe tener al menos 3 caracteres';
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulario antes de enviar
    if (!validarFormulario()) {
      return;
    }

    // Preparar datos para enviar
    const datosEnviar = {
      idEnvase: formulario.idEnvase.trim().toUpperCase(),
      nomEnvase: formulario.nomEnvase.trim()
    };

    try {
      if (modoEdicion) {
        await axios.put(`${API_URL}/${formulario.idEnvase}`, datosEnviar);
        alert('Envase actualizado exitosamente');
      } else {
        await axios.post(API_URL, datosEnviar);
        alert('Envase creado exitosamente');
      }
      cargarDatos();
      cerrarModal();
    } catch (error) {
      console.error('Error al guardar:', error);
      if (error.response) {
        alert(`Error al guardar: ${error.response.data.message || 'Error del servidor'}`);
      } else if (error.request) {
        alert('Error de conexión con el servidor');
      } else {
        alert('Error al guardar el envase');
      }
    }
  };

  const eliminar = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este envase?')) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        alert('Envase eliminado exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar el envase');
      }
    }
  };

  return (
    <div className="envase-container">
      <div className="header-seccion">
        <h2>Envases</h2>
        <div className="acciones-header">
          <input
            type="text"
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input-busqueda"
          />
          <button onClick={abrirModalNuevo} className="btn btn-nuevo">
            ➕ Nuevo
          </button>
          <button onClick={exportarExcel} className="btn btn-exportar">
            📊 Exportar Excel
          </button>
          <button onClick={abrirImportar} className="btn btn-exportar"
            disabled={importando}
            style={{ background: '#7B1FA2' }}
            title="Importar envases desde Excel (upsert por id_envase)">
            {importando ? '⏳ Importando...' : '📥 Importar Excel'}
          </button>
          <input type="file" accept=".xlsx,.xls" ref={inputImportarRef}
            onChange={onArchivoImportar} style={{ display: 'none' }} />
        </div>
      </div>

      <div className="tabla-container">
        <table className="tabla-datos">
          <thead>
            <tr>
              <th onClick={() => ordenarPor('idEnvase')} className="th-ordenable">
                ID Envase {columnaOrden === 'idEnvase' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('nomEnvase')} className="th-ordenable">
                Nombre {columnaOrden === 'nomEnvase' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {datosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="3" className="sin-datos">No hay datos para mostrar</td>
              </tr>
            ) : (
              datosFiltrados.map(item => (
                <tr key={item.idEnvase}>
                  <td>{item.idEnvase}</td>
                  <td>{item.nomEnvase}</td>
                  <td>
                    <button
                      onClick={() => abrirModalEditar(item)}
                      className="btn-accion btn-editar"
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => eliminar(item.idEnvase)}
                      className="btn-accion btn-eliminar"
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="footer-tabla" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span>Total de registros: {total}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => irAPagina(1)} disabled={pagina === 1} className="btn-paginacion">«</button>
          <button onClick={() => irAPagina(pagina - 1)} disabled={pagina === 1} className="btn-paginacion">‹</button>
          <span style={{ fontSize: '13px' }}>Pagina {pagina} de {totalPaginas}</span>
          <button onClick={() => irAPagina(pagina + 1)} disabled={pagina >= totalPaginas} className="btn-paginacion">›</button>
          <button onClick={() => irAPagina(totalPaginas)} disabled={pagina >= totalPaginas} className="btn-paginacion">»</button>
          <select value={porPagina} onChange={(e) => cambiarPorPagina(Number(e.target.value))}
            style={{ marginLeft: '8px', padding: '4px' }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pag</option>)}
          </select>
        </div>
      </div>

      {/* Modal */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-contenido" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modoEdicion ? 'Editar Envase' : 'Nuevo Envase'}</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✖</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>ID Envase *</label>
                <input
                  type="text"
                  name="idEnvase"
                  value={formulario.idEnvase}
                  onChange={handleChange}
                  maxLength="3"
                  disabled={modoEdicion}
                  className={`form-input ${errores.idEnvase ? 'form-input-error' : ''}`}
                  placeholder="3 caracteres"
                />
                {errores.idEnvase && (
                  <small className="form-error">{errores.idEnvase}</small>
                )}
              </div>

              <div className="form-group">
                <label>Nombre Envase *</label>
                <input
                  type="text"
                  name="nomEnvase"
                  value={formulario.nomEnvase}
                  onChange={handleChange}
                  maxLength="35"
                  className={`form-input ${errores.nomEnvase ? 'form-input-error' : ''}`}
                />
                {errores.nomEnvase && (
                  <small className="form-error">{errores.nomEnvase}</small>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={cerrarModal} className="btn btn-cancelar">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-guardar">
                  {modoEdicion ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Envase;
